import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import './VerticalMenu.css';
import './BottomNav.css';
import Logo from '../assets/siteLogo.png';
import UserListIcon from '../assets/icons/user-list.svg';
import UserIcon from '../assets/icons/user.svg';
import GearIcon from '../assets/icons/gear.svg';
import SignOutIcon from '../assets/icons/sign-out.svg';
import HouseIcon from '../assets/icons/house.svg';
import WarningIcon from '../assets/icons/warning.svg';
import NotepadIcon from '../assets/icons/notepad.svg';
import React from 'react';
import { isMobileView } from '../utils/deviceUtils';
import { useAuth } from '../auth/AuthContext';
import FlashMessage from './FlashMessage';

const VerticalMenu = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const hamburgerMenuRef = React.useRef<HTMLDivElement>(null);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = React.useState(false);
  const [flashMessage, setFlashMessage] = React.useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const isMobile = isMobileView();
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const { token, logout } = useAuth();
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    setFlashMessage({ message: 'You have been logged out successfully', type: 'success' });
    setIsAccountDropdownOpen(false);
    setIsMenuOpen(false);
    setTimeout(async () => {
      await logout();
      navigate('/');
    }, 1000);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Account dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsAccountDropdownOpen(false);
      }
      // Hamburger menu (mobile)
      if (isMenuOpen && hamburgerMenuRef.current && !hamburgerMenuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  return (
    <>
      {flashMessage && (
        <FlashMessage
          message={flashMessage.message}
          type={flashMessage.type}
          onClose={() => setFlashMessage(null)}
        />
      )}
      {/* Sticky top-right auth button — visible on desktop */}
      {!isMobile && (
        <div className="auth-corner" ref={dropdownRef}>
          {!token ? (
            <NavLink to="/login" className="auth-link">
              <img src={UserListIcon} alt="" className="auth-link-icon" />
              Log in
            </NavLink>
          ) : (
            <div className="account-dropdown-container">
              <button
                type="button"
                className="auth-link"
                onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
              >
                <img src={UserListIcon} alt="" className="auth-link-icon" style={{ filter: 'invert(0%)' }} />
                Account
              </button>
              {isAccountDropdownOpen && (
                <div className="account-dropdown">
                  <button
                    type="button"
                    className="account-dropdown-item"
                    onClick={() => {
                      setIsAccountDropdownOpen(false);
                      navigate('/profile');
                    }}
                  >
                    <img src={UserIcon} alt="" className="dropdown-item-icon" />
                    Profile
                  </button>
                  <button
                    type="button"
                    className="account-dropdown-item"
                    onClick={() => {
                      setIsAccountDropdownOpen(false);
                      navigate('/settings');
                    }}
                  >
                    <img src={GearIcon} alt="" className="dropdown-item-icon" />
                    Settings
                  </button>
                  <button
                    type="button"
                    className="account-dropdown-item"
                    onClick={handleLogout}
                  >
                    <img src={SignOutIcon} alt="" className="dropdown-item-icon" />
                    Log out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

    <nav className="vertical-menu">
      {/* Logo */}
      <div>
        <Link to="/">
          <img src={Logo} alt="Logo" className="logo" />
        </Link>
      </div>

      {/* Mobile Hamburger Menu Icon */}
      {isMobile && (
        <div className="hamburger" onClick={toggleMenu}>
          ☰ {/* You can replace this with an icon from a library like FontAwesome */}
        </div>
      )}

      {/* Links for Desktop */}
      {!isMobile && (
        <div className="vertical-links">
          <NavLink to="/" className="vertical-link" end>Home</NavLink>
          <NavLink to="/recipes" className="vertical-link">Recipes</NavLink>
          <NavLink to="/supplements" className="vertical-link">Supplements</NavLink>
          <NavLink to="/food-to-avoid" className="vertical-link">Food to Avoid</NavLink>
          <NavLink to="/tips" className="vertical-link">Guides & Tips</NavLink>
          <NavLink to="/about" className="vertical-link">About</NavLink>
        </div>
      )}

      {/* Mobile Navigation (Hamburger Menu) */}
      {isMobile && (
        <>
          {/* Overlay */}
          <div
            className={`hamburger-overlay ${isMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(false)}
          />
          
          {/* Side Panel */}
          <div
            className={`hamburger-menu ${isMenuOpen ? 'active' : ''}`}
            ref={hamburgerMenuRef}
          >
            <div className="hamburger-menu-header">
              <h2 className="hamburger-menu-title">Menu</h2>
              <button className="hamburger-close" onClick={() => setIsMenuOpen(false)}>
                ×
              </button>
            </div>
            <ul>
              <li><Link to="/supplements" className={location.pathname === '/supplements' ? 'active' : ''} onClick={() => setIsMenuOpen(false)}>Supplements</Link></li>
              <li><Link to="/tips" className={location.pathname === '/tips' ? 'active' : ''} onClick={() => setIsMenuOpen(false)}>Guides & Tips</Link></li>
              <li><Link to="/about" className={location.pathname === '/about' ? 'active' : ''} onClick={() => setIsMenuOpen(false)}>About</Link></li>
              <li>
                <button onClick={() => { setIsMenuOpen(false); navigate('/settings'); }}>
                  Settings
                </button>
              </li>
            </ul>
          </div>
        </>
      )}

      {/* Mobile Bottom Navigation Bar */}
      {isMobile && (
        <div className="mobile-bottom-nav">
          <Link to="/" className={`bottom-nav-item ${location.pathname === '/' ? 'active' : ''}`}>
            <img src={HouseIcon} alt="" className="bottom-nav-icon" />
            <span className="bottom-nav-label">Home</span>
          </Link>
          <Link to="/recipes" className={`bottom-nav-item ${location.pathname.startsWith('/recipes') ? 'active' : ''}`}>
            <img src={NotepadIcon} alt="" className="bottom-nav-icon" />
            <span className="bottom-nav-label">Recipes</span>
          </Link>
          <Link to="/food-to-avoid" className={`bottom-nav-item ${location.pathname === '/food-to-avoid' ? 'active' : ''}`}>
            <img src={WarningIcon} alt="" className="bottom-nav-icon" />
            <span className="bottom-nav-label">Avoid</span>
          </Link>
          {token ? (
            <Link to="/profile" className={`bottom-nav-item ${location.pathname === '/profile' || location.pathname === '/settings' ? 'active' : ''}`}>
              <img src={UserIcon} alt="" className="bottom-nav-icon" />
              <span className="bottom-nav-label">Account</span>
            </Link>
          ) : (
            <Link to="/login" className={`bottom-nav-item ${location.pathname === '/login' ? 'active' : ''}`}>
              <img src={UserIcon} alt="" className="bottom-nav-icon" />
              <span className="bottom-nav-label">Login</span>
            </Link>
          )}
        </div>
      )}
    </nav>
    </>
  );
};

export default VerticalMenu;
